import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hiddenField", "container"]
  // flat: hide the nesting controls (drill-down + allow-parent). Used by in-line forms, whose FormField
  // stores a flat list of string options and can't represent nested choices.
  static values = { flat: Boolean }

  connect() {
    const data = JSON.parse(this.hiddenFieldTarget.value || '{}')

    if (data.nested && Array.isArray(data.values)) {
      this.nodes = data.values
    } else if (Array.isArray(data.values)) {
      this.nodes = data.values.map(v => ({ label: v }))
    } else {
      this.nodes = []
    }

    this.allowParentValues = !!data.allow_parent_values
    this.currentPath = []
    this.render()
  }

  // --- Sync state to hidden field ---

  sync() {
    const cleaned = {}
    const pruned = this.prune(this.nodes)

    if (pruned.length === 0) {
      this.hiddenFieldTarget.value = '{}'
      return
    }

    if (this.hasNesting(pruned)) {
      cleaned.values = pruned
      cleaned.nested = true
      if (this.allowParentValues) cleaned.allow_parent_values = true
    } else {
      cleaned.values = pruned.map(n => n.label)
    }

    this.hiddenFieldTarget.value = JSON.stringify(cleaned)
  }

  render() {
    this.containerTarget.innerHTML = this.buildHTML()
    this.sync()
  }

  // --- Actions ---

  addValue(e) {
    e.preventDefault()
    const input = this.containerTarget.querySelector('[data-add-input] input')
    const val = input.value.trim()
    if (!val) return

    this.currentChildren().push({ label: val })
    input.value = ''
    this.render()
    this.containerTarget.querySelector('[data-add-input] input')?.focus()
  }

  removeNode(e) {
    e.preventDefault()
    const idx = parseInt(e.target.closest('[data-node-idx]').dataset.nodeIdx)
    this.currentChildren().splice(idx, 1)
    this.render()
  }

  renameNode(e) {
    const idx = parseInt(e.target.closest('[data-node-idx]').dataset.nodeIdx)
    this.currentChildren()[idx].label = e.target.value
    this.sync()
  }

  renameParent(e) {
    this.currentParent().label = e.target.value
    this.sync()
  }

  drillDown(e) {
    e.preventDefault()
    const idx = parseInt(e.target.closest('[data-node-idx]').dataset.nodeIdx)
    const node = this.currentChildren()[idx]
    if (!node.children) node.children = []
    this.currentPath.push(idx)
    this.render()
  }

  navigateTo(e) {
    e.preventDefault()
    const depth = parseInt(e.target.closest('[data-nav-depth]').dataset.navDepth)
    this.currentPath = this.currentPath.slice(0, depth)
    this.render()
  }

  toggleAllowParent(e) {
    this.allowParentValues = e.target.checked
    this.sync()
  }

  // --- Tree helpers ---

  currentParent() {
    if (this.currentPath.length === 0) return null
    let node = this.nodes[this.currentPath[0]]
    for (let i = 1; i < this.currentPath.length; i++) {
      node = node.children[this.currentPath[i]]
    }
    return node
  }

  currentChildren() {
    const parent = this.currentParent()
    if (!parent) return this.nodes
    if (!parent.children) parent.children = []
    return parent.children
  }

  breadcrumbItems() {
    const items = [{ label: 'Options', depth: 0 }]
    let nodes = this.nodes
    for (let i = 0; i < this.currentPath.length; i++) {
      const node = nodes[this.currentPath[i]]
      items.push({ label: node.label || '(unnamed)', depth: i + 1 })
      nodes = node.children || []
    }
    return items
  }

  hasNesting(nodes) {
    for (const n of nodes) {
      if (n.children?.length > 0) return true
      if (n.children && this.hasNesting(n.children)) return true
    }
    return false
  }

  prune(nodes) {
    return nodes
      .filter(n => n.label?.trim())
      .map(n => {
        if (n.children?.length > 0) {
          const pruned = this.prune(n.children)
          return pruned.length > 0 ? { label: n.label, children: pruned } : { label: n.label }
        }
        return { label: n.label }
      })
  }

  // --- HTML ---

  buildHTML() {
    const isRoot = this.currentPath.length === 0
    const children = this.currentChildren()
    let html = ''

    // Label or breadcrumb path
    if (isRoot) {
      html += '<label class="!mt-0">Options</label>'
    } else {
      const crumbs = this.breadcrumbItems()
      html += '<div class="flex items-center gap-1 text-xs md:text-base font-medium tracking-tight">'
      crumbs.forEach((item, i) => {
        if (i > 0) html += '<span class="text-gray-250 mx-0.5">/</span>'
        if (i < crumbs.length - 1) {
          html += `<button type="button" data-nav-depth="${item.depth}" data-action="field-options#navigateTo"
                     class="text-blue-secondary hover:opacity-70">${this.esc(item.label)}</button>`
        } else {
          html += `<span class="text-gray-400">${this.esc(item.label)}</span>`
        }
      })
      html += '</div>'

      // Parent field (editable)
      const parent = this.currentParent()
      html += `
        <div class="flex items-center gap-2 mt-1">
          <div class="input-wrapper input-base flex-1 !mt-0">
            <input type="text" value="${this.esc(parent.label)}"
                   class="px-4 py-3 text-sm md:text-base border-0 font-medium w-full focus:ring-0 bg-transparent rounded-xl"
                   data-action="change->field-options#renameParent"
                   placeholder="Parent option">
          </div>
        </div>`
    }

    // Children list (indented when inside a parent)
    const indentClass = isRoot ? 'mt-1' : 'mt-2 ml-6'
    html += `<div class="space-y-2 ${indentClass}">`

    children.forEach((node, i) => {
      const childCount = node.children?.length || 0
      html += `
        <div data-node-idx="${i}" class="flex items-center gap-2">
          <div class="input-wrapper input-base flex-1 !mt-0">
            <input type="text" value="${this.esc(node.label)}"
                   class="px-4 py-3 text-sm md:text-base border-0 font-normal w-full focus:ring-0 bg-transparent rounded-xl"
                   data-action="change->field-options#renameNode"
                   placeholder="Option name">
          </div>
          ${this.flatValue ? '' : `<button type="button" data-action="field-options#drillDown"
                  class="flex items-center gap-0.5 px-1.5 py-1.5 text-gray-88 hover:text-blue-secondary rounded-lg transition-colors flex-shrink-0"
                  title="Edit sub-options">
            ${childCount > 0 ? `<span class="text-xs">${childCount}</span>` : ''}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>`}
          <button type="button" data-action="field-options#removeNode"
                  class="p-1.5 text-gray-88 hover:text-red-500 rounded-lg transition-colors flex-shrink-0"
                  title="Remove">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>`
    })

    // Add input
    html += `
      <div data-add-input>
        <div class="input-wrapper input-base !mt-0">
          <input type="text" placeholder="Add ${isRoot ? 'option' : 'sub-option'}…"
                 class="px-4 py-3 text-sm md:text-base border-0 font-normal w-full focus:ring-0 bg-transparent rounded-xl"
                 data-action="keydown.enter->field-options#addValue">
        </div>
      </div>`

    html += '</div>'

    // Allow parent values toggle (nesting only)
    if (!this.flatValue && this.hasNesting(this.nodes)) {
      html += `
        <div class="toggle-row !p-3 mt-3">
          <div class="toggle-row-content">
            <label class="toggle-row-label">Allow Parent Values</label>
            <p class="toggle-row-description">When off, only the final sub-options can be selected. Turn on to also allow selecting top-level options.</p>
          </div>
          <label class="toggle-switch !mt-0">
            <input type="checkbox" ${this.allowParentValues ? 'checked' : ''}
                   data-action="field-options#toggleAllowParent"
                   class="sr-only">
            <div class="toggle-switch-track"></div>
          </label>
        </div>`
    }

    return html
  }

  esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
  }
}
